import { useEffect } from 'react';
import {
	LOOM_MEETING_NUDGE_REMINDER_REQUESTED_EVENT,
	type LoomMeetingNudgeReminderRequest,
} from '@post-office/message-template--twc-loom-meeting-nudge-sidenav/reminder-event';

import { LOOM_NUDGE_REMIND_ME } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';

import { markLocalMeetingSignalReminderPending } from './useIsLocalMeetingSignalReminderPending.ts';

type ReminderRequestedEvent = CustomEvent<LoomMeetingNudgeReminderRequest>;

const isReminderRequest = (detail: unknown): detail is LoomMeetingNudgeReminderRequest =>
	typeof detail === 'object' &&
	detail !== null &&
	'handled' in detail &&
	typeof detail.handled === 'boolean';

/**
 * Listens for reminder requests from the Post Office nudge and persists the intent for the next
 * regular-page publish or page load. Requests are marked handled so multiple mounted meeting-signal
 * evaluators cannot persist the same intent more than once.
 */
export const useMeetingSignalReminderRequestListener = (): void => {
	const { setOnboardingState } = useSetOnboardingState();

	useEffect(() => {
		const handleReminderRequested = (event: Event): void => {
			if (!(event instanceof CustomEvent) || !isReminderRequest(event.detail)) {
				return;
			}

			const reminderRequest: ReminderRequestedEvent['detail'] = event.detail;
			if (reminderRequest.handled) {
				return;
			}
			reminderRequest.handled = true;
			markLocalMeetingSignalReminderPending();
			setOnboardingState({ key: LOOM_NUDGE_REMIND_ME, value: 'true' });
		};

		globalThis.window?.addEventListener(
			LOOM_MEETING_NUDGE_REMINDER_REQUESTED_EVENT,
			handleReminderRequested,
		);
		return () => {
			globalThis.window?.removeEventListener(
				LOOM_MEETING_NUDGE_REMINDER_REQUESTED_EVENT,
				handleReminderRequested,
			);
		};
	}, [setOnboardingState]);
};
